import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Banner,
  Button,
  buttonVariants,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  sonnerToast,
  useDocumentTitle,
} from "@simplicity/ui";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type {
  AdhocTransformRun,
  ConfigRevision,
  Project,
  TransformDefinition,
} from "../../configTypes";
import { LAYER_OPTIONS } from "../../configTypes";
import { isTerminalAdhocStatus, useAdhocRunPolling } from "../../hooks/useAdhocRunPolling";
import { useDetailForm } from "../../hooks/useDetailForm";
import { DESTRUCTIVE_BUTTON_CLASS } from "../../utils";
import { Breadcrumbs } from "./Breadcrumbs";
import { getLayerBadgeStyle } from "./layerStyle";
import { ParamsEditor } from "./ParamsEditor";
import { SqlTextarea } from "./SqlTextarea";
import { useDirtyForm } from "./useDirtyForm";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";
import { TransformVersionHistoryList } from "./version-history/TransformVersionHistoryList";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function baselineFromDef(td: TransformDefinition) {
  return {
    name: td.name,
    layer: td.layer,
    sqlTemplate: td.sqlTemplate,
    paramsJson: JSON.stringify(td.params),
    enabled: String(td.enabled),
  };
}

function fetchRevisions(id: string, setRevisions: (r: ConfigRevision[]) => void) {
  apiFetch<ConfigRevision[]>(`/api/transform-definitions/${id}/config-revisions`)
    .then((r) => setRevisions(r))
    .catch(() => {}); // Revisions are supplementary — silently fail.
}

/** Read-only output fields (auto-detected from SQL). */
function OutputRow({ definition }: { definition: TransformDefinition }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="td-outputDataset" className="form-label">
          Dataset
        </Label>
        <Input
          id="td-outputDataset"
          value={definition.outputDataset}
          readOnly
          className="font-mono bg-muted"
        />
      </div>
      <div>
        <Label htmlFor="td-outputTable" className="form-label">
          Table
        </Label>
        <Input
          id="td-outputTable"
          value={definition.outputTable}
          readOnly
          className="font-mono bg-muted"
        />
      </div>
    </div>
  );
}

/** Auto-detected dependencies table (read-only). */
function DependenciesSection({ definition }: { definition: TransformDefinition }) {
  if (definition.dependencies.length === 0) {
    return (
      <div className="rounded border border-border px-3 py-2 text-sm italic text-muted-foreground">
        Dependencies will be auto-detected from your SQL on save.
      </div>
    );
  }
  return (
    <table className="admin-table">
      <thead>
        <tr>
          <th>Source Dataset</th>
          <th>Source Table</th>
        </tr>
      </thead>
      <tbody>
        {definition.dependencies.map((d) => (
          <tr key={d.id}>
            <td className="font-mono text-sm">{d.sourceDataset}</td>
            <td className="font-mono text-sm">{d.sourceTable}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Identity row: name, layer, enabled — with banner-clearing onChange. */
function IdentityRow({
  name,
  layer,
  enabled,
  onNameChange,
  onLayerChange,
  onEnabledChange,
}: {
  name: string;
  layer: string;
  enabled: boolean;
  onNameChange: (v: string) => void;
  onLayerChange: (v: string) => void;
  onEnabledChange: (v: boolean) => void;
}) {
  return (
    <div className="mb-4 grid grid-cols-[1fr_160px_auto] gap-4">
      <div>
        <Label htmlFor="td-name" className="form-label">
          Name
        </Label>
        <Input
          id="td-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. incidents_latest"
        />
      </div>
      <div>
        <Label htmlFor="td-layer" className="form-label">
          Layer
        </Label>
        {/* SIM-1689: trigger paints with the layer's badge hue so the
            standalone <LayerBadge> in the heading is no longer needed —
            "current layer" is communicated by the dropdown's own color. */}
        <Select value={layer} onValueChange={onLayerChange}>
          <SelectTrigger
            id="td-layer"
            style={getLayerBadgeStyle(layer)}
            data-testid="td-layer-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LAYER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end pb-1">
        <Label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox checked={enabled} onCheckedChange={(v) => onEnabledChange(v === true)} />
          Enabled
        </Label>
      </div>
    </div>
  );
}

/** Delete confirmation dialog. */
function DeleteDialog({
  open,
  onOpenChange,
  definitionName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  definitionName: string;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete transform definition?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete &ldquo;{definitionName}&rdquo; and its dependency records.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={onConfirm}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Inline status indicator for an adhoc transform run. */
function AdhocRunStatus({ run, lostContact }: { run: AdhocTransformRun; lostContact?: boolean }) {
  if (lostContact && !isTerminalAdhocStatus(run.status)) {
    return (
      <p className="mt-2 text-sm text-destructive">
        Lost contact with this run — it is still executing. Reload to check the result.
      </p>
    );
  }
  if (run.status === "pending") {
    return <p className="mt-2 text-sm text-muted-foreground">Queued — waiting for runner...</p>;
  }
  if (run.status === "claimed") {
    return <p className="mt-2 text-sm text-muted-foreground">Running...</p>;
  }
  if (run.status === "completed") {
    const duration =
      run.completedAt && run.requestedAt
        ? Math.round(
            (new Date(run.completedAt).getTime() - new Date(run.requestedAt).getTime()) / 1000,
          )
        : null;
    return (
      <p className="mt-2 text-sm text-success">
        Completed{duration != null ? ` in ${duration}s` : ""}
        {run.bytesProcessed != null ? ` (${formatBytes(run.bytesProcessed)})` : ""}
      </p>
    );
  }
  if (run.status === "failed") {
    return (
      <p className="mt-2 text-sm text-destructive">Failed: {run.errorMessage ?? "Unknown error"}</p>
    );
  }
  return null;
}

/** Hook for triggering and polling an adhoc transform run. */
function useAdhocRun(transformDefinitionId: string | undefined) {
  const polling = useAdhocRunPolling({
    historyKey: transformDefinitionId,
    fetchHistory: () =>
      apiFetch<AdhocTransformRun[]>(
        `/api/transform-definitions/${transformDefinitionId}/adhoc-runs`,
      ),
  });

  async function triggerRun() {
    if (!transformDefinitionId) return;
    try {
      const run = await apiFetch<AdhocTransformRun>("/api/adhoc-transform-runs", {
        method: "POST",
        body: JSON.stringify({ transformDefinitionId }),
      });
      polling.adoptTriggeredRun(run);
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to queue transform run");
    }
  }

  return { ...polling, triggerRun };
}

/** Form-field state and helpers grouped together so the parent hook stays small. */
function useTransformFormFields() {
  const [name, setName] = useState("");
  const [layer, setLayer] = useState("silver-canon");
  const [sqlTemplate, setSqlTemplate] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(true);

  const applyFromServer = useCallback((td: TransformDefinition) => {
    setName(td.name);
    setLayer(td.layer);
    setSqlTemplate(td.sqlTemplate);
    setParams(td.params);
    setEnabled(td.enabled);
  }, []);

  return {
    name,
    setName,
    layer,
    setLayer,
    sqlTemplate,
    setSqlTemplate,
    params,
    setParams,
    enabled,
    setEnabled,
    applyFromServer,
  };
}

/** Custom hook encapsulating transform definition fetch, save, and delete logic. */
function useTransformDefinition(id: string | undefined) {
  const navigate = useNavigate();
  const fields = useTransformFormFields();

  const [definition, setDefinition] = useState<TransformDefinition | null>(null);
  const [projectName, setProjectName] = useState("");
  const [revisions, setRevisions] = useState<ConfigRevision[]>([]);

  const { isDirty, setBaseline, resetBaseline } = useDirtyForm({
    name: fields.name,
    layer: fields.layer,
    sqlTemplate: fields.sqlTemplate,
    paramsJson: JSON.stringify(fields.params),
    enabled: String(fields.enabled),
  });

  const form = useDetailForm<TransformDefinition>({
    id,
    fetcher: (tid) => apiFetch<TransformDefinition>(`/api/transform-definitions/${tid}`),
    onLoaded: async (td) => {
      setDefinition(td);
      fields.applyFromServer(td);
      setBaseline(baselineFromDef(td));
      const project = await apiFetch<Project>(`/api/projects/${td.projectId}`);
      setProjectName(project.name);
      if (id) fetchRevisions(id, setRevisions);
    },
    saver: () =>
      apiFetch<TransformDefinition>(`/api/transform-definitions/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: fields.name.trim(),
          layer: fields.layer,
          sqlTemplate: fields.sqlTemplate,
          params: fields.params,
          enabled: fields.enabled,
        }),
      }),
    onSaved: (updated) => {
      setDefinition(updated);
      fields.applyFromServer(updated);
      resetBaseline();
      if (id) fetchRevisions(id, setRevisions);
    },
    successMessage: "Transform saved. Dependencies updated.",
  });

  async function handleDelete() {
    try {
      await apiFetch(`/api/transform-definitions/${id}`, { method: "DELETE" });
      sonnerToast.info("Transform definition deleted");
      void navigate(definition ? `/config/projects/${definition.projectId}` : "/config/projects");
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return {
    ...fields,
    definition,
    projectName,
    loading: form.loading,
    error: form.error,
    revisions,
    saving: form.saving,
    saveLabel: form.saveLabel,
    notification: form.notification,
    clearBanner: form.clearNotification,
    isDirty,
    save: form.save,
    handleDelete,
  };
}

// SIM-1689 followup: everything stays in the left col with the editor —
// identity row, SQL, timestamps, action bar — so the action bar is
// always visible just below the editor regardless of right-col height.
// Right col is the inspector: four stacked sections instead of tabs.
const BOTTOM_RESERVE_PX = 130;

function InspectSection({
  heading,
  testid,
  children,
}: {
  heading: string;
  testid?: string;
  children: React.ReactNode;
}) {
  return (
    <section data-testid={testid} className="rounded-md border border-border/50 bg-muted/10 p-4">
      <h3 className="mb-3 font-semibold text-foreground">{heading}</h3>
      {children}
    </section>
  );
}

interface LeftColProps {
  definition: TransformDefinition;
  sqlTemplate: string;
  saving: boolean;
  saveLabel: string;
  isDirty: boolean;
  name: string;
  layer: string;
  enabled: boolean;
  onNameChange: (v: string) => void;
  onLayerChange: (v: string) => void;
  onEnabledChange: (v: boolean) => void;
  onSqlChange: (v: string) => void;
  onDelete: () => void;
  onRunNow: () => void;
  adhocRun: AdhocTransformRun | null;
  isRunning: boolean;
  lostContact: boolean;
}

/** Left column: identity → SQL → timestamps → action bar. The action bar
 *  sits just below the JS-sized editor, so it's always visible. */
function LeftCol(props: LeftColProps) {
  const {
    definition,
    sqlTemplate,
    saving,
    saveLabel,
    isDirty,
    name,
    adhocRun,
    isRunning,
    lostContact,
  } = props;
  return (
    <div className="lg:col-span-2" data-testid="td-left-col">
      <IdentityRow
        name={name}
        layer={props.layer}
        enabled={props.enabled}
        onNameChange={props.onNameChange}
        onLayerChange={props.onLayerChange}
        onEnabledChange={props.onEnabledChange}
      />
      <div className="mb-4">
        <Label htmlFor="td-sql" className="form-label">
          SQL Template
        </Label>
        <SqlTextarea
          id="td-sql"
          value={sqlTemplate}
          onChange={props.onSqlChange}
          bottomReservePx={BOTTOM_RESERVE_PX}
        />
      </div>
      <div className="mb-6 flex gap-6 text-xs text-muted-foreground">
        <span>Created: {formatDate(definition.createdAt)}</span>
        <span>Updated: {formatDate(definition.updatedAt)}</span>
        <span>Order: {definition.order}</span>
      </div>
      <div className="form-actions">
        <Button
          type="button"
          variant="outline"
          onClick={props.onRunNow}
          disabled={saving || isRunning}
        >
          {isRunning ? "Running..." : "Run now"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className={DESTRUCTIVE_BUTTON_CLASS}
          onClick={props.onDelete}
        >
          Delete
        </Button>
        <Button type="submit" disabled={saving || !isDirty || !name.trim() || !sqlTemplate.trim()}>
          {saving ? "Saving..." : saveLabel}
        </Button>
      </div>
      {adhocRun && <AdhocRunStatus run={adhocRun} lostContact={lostContact} />}
    </div>
  );
}

interface RightColProps {
  definition: TransformDefinition;
  params: Record<string, string>;
  sqlTemplate: string;
  onParamsChange: (v: Record<string, string>) => void;
  historyContent: React.ReactNode;
}

/** Right column: four stacked inspector sections. On narrow the grid
 *  collapses and these sections appear below the action bar in document order. */
function RightCol({
  definition,
  params,
  sqlTemplate,
  onParamsChange,
  historyContent,
}: RightColProps) {
  return (
    <div className="mt-6 flex flex-col gap-4 lg:col-span-1 lg:mt-0" data-testid="td-inspect-col">
      <InspectSection heading="Schema / table" testid="td-inspect-schema">
        <OutputRow definition={definition} />
      </InspectSection>
      <InspectSection heading="Parameters" testid="td-inspect-parameters">
        <ParamsEditor params={params} onChange={onParamsChange} sqlTemplate={sqlTemplate} />
      </InspectSection>
      <InspectSection heading="Derived dependencies" testid="td-inspect-dependencies">
        <DependenciesSection definition={definition} />
      </InspectSection>
      <InspectSection heading="Version history" testid="td-inspect-history">
        {historyContent}
      </InspectSection>
    </div>
  );
}

/** Two-column layout: left col holds identity + SQL + timestamps + actions;
 *  right col is the four-section inspector. */
function FormFieldsAndActions(
  props: LeftColProps & RightColProps & { historyContent: React.ReactNode },
) {
  return (
    <div className="lg:grid lg:grid-cols-3 lg:items-start lg:gap-4">
      <LeftCol {...props} />
      <RightCol {...props} />
    </div>
  );
}

/** Edit form body, given the loaded definition + form-state object. */
function TransformEditForm({
  definition,
  td,
  adhoc,
  onDeleteOpen,
}: {
  definition: TransformDefinition;
  td: ReturnType<typeof useTransformDefinition>;
  adhoc: ReturnType<typeof useAdhocRun>;
  onDeleteOpen: () => void;
}) {
  return (
    <div className="form-card mb-6">
      {/* SIM-1689: header row collapses "Edit Transform" + ID into one line
          (was 2 rows: title-with-badge + standalone ID below). LayerBadge
          moves into the Layer Select trigger via getLayerBadgeStyle, so the
          standalone badge in the heading is gone. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg text-foreground">Edit Transform</h2>
        <span className="truncate font-mono text-xs text-muted-foreground" title={definition.id}>
          ID: {definition.id}
        </span>
      </div>
      {td.notification && (
        <Banner variant={td.notification.variant} onDismiss={td.clearBanner} className="mb-4">
          {td.notification.message}
        </Banner>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void td.save();
        }}
      >
        {/* SIM-1689 followup: IdentityRow moved INTO FormFieldsAndActions'
            left column so identity / SQL / actions stay in the same column
            on lg+ layouts. */}
        <FormFieldsAndActions
          definition={definition}
          sqlTemplate={td.sqlTemplate}
          params={td.params}
          saving={td.saving}
          saveLabel={td.saveLabel}
          isDirty={td.isDirty}
          name={td.name}
          layer={td.layer}
          enabled={td.enabled}
          onNameChange={(v) => {
            td.setName(v);
            td.clearBanner();
          }}
          onLayerChange={(v) => {
            td.setLayer(v);
            td.clearBanner();
          }}
          onEnabledChange={(v) => {
            td.setEnabled(v);
            td.clearBanner();
          }}
          onSqlChange={(v) => {
            td.setSqlTemplate(v);
            td.clearBanner();
          }}
          onParamsChange={(p) => {
            td.setParams(p);
            td.clearBanner();
          }}
          onDelete={onDeleteOpen}
          onRunNow={() => void adhoc.triggerRun()}
          adhocRun={adhoc.latestRun}
          isRunning={adhoc.isRunning}
          lostContact={adhoc.lostContact}
          historyContent={
            <TransformVersionHistoryList
              revisions={td.revisions}
              currentSqlTemplate={td.sqlTemplate}
              currentParams={td.params}
              onRestore={(sqlTemplate, params) => {
                td.setSqlTemplate(sqlTemplate);
                td.setParams(params);
                td.clearBanner();
              }}
            />
          }
        />
      </form>
    </div>
  );
}

export function TransformDefinitionDetails() {
  const { id } = useParams<{ id: string }>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const td = useTransformDefinition(id);
  const adhoc = useAdhocRun(id);
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(td.isDirty);

  useDocumentTitle(td.definition?.name ?? null, { suffix: " | Simplicity Admin" });

  if (td.loading) return <div className="empty-state">Loading transform definition...</div>;
  if (td.error || !td.definition) {
    return (
      <div className="empty-state text-destructive">
        {td.error ?? "Transform definition not found"}
      </div>
    );
  }

  const { definition } = td;

  return (
    // SIM-1689 follow-up: max-w-[1800px] (was max-w-4xl / 896px) so the form
    // uses most of the screen on wide monitors. The Inspect column at 33%
    // was too cramped at 896px form-width to fit four tab labels in one row.
    <div className="mx-auto w-full max-w-[1800px]" data-testid="td-page-wrapper">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          {
            label: td.projectName || "...",
            to: `/config/projects/${definition.projectId}#transforms`,
          },
          { label: definition.name },
        ]}
      />
      <TransformEditForm
        definition={definition}
        td={td}
        adhoc={adhoc}
        onDeleteOpen={() => setDeleteOpen(true)}
      />
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        definitionName={definition.name}
        onConfirm={() => void td.handleDelete()}
      />
      {unsavedChangesDialog}
    </div>
  );
}
